import * as Sentry from '@sentry/browser'
import Calendar from '@toast-ui/react-calendar'
import { HeaderComponent } from 'components/header/component'
import { ToggleOff } from 'components/icons/toggleoff'
import { ToggleOn } from 'components/icons/toggleon'
import { Loading } from 'components/loading'
import * as Flash from 'components/flash'
import { RasaContext } from 'context'
import { addMonths, endOfMonth, format, isBefore, startOfMonth } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import { Dataset } from 'generic/dataset'
import * as GenericRedux from 'generic/genericRedux'
import { isTruthy } from 'generic/utility'
import isNil from 'lodash/isNil'
import React, { Component, ComponentType } from 'react'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { TimePicker } from '@progress/kendo-react-dateinputs'
import { formatDateForES, validateTz } from 'shared_server_client/dates'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { DEFAULT_TIMEZONE, ScheduleFrequency } from 'shared_server_client/constants'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { HolidaySchedule, UpcomingIssue } from 'shared_server_client/types/schedule'
import 'tui-calendar/dist/tui-calendar.css'
import 'tui-time-picker/dist/tui-time-picker.css'
import './_styles.scss'
import { CALENDAR_HEIGHT, SCHEDULE, UPCOMING_NEWSLETTERS } from './constants'
import { CalendarModal } from './modals'
import { getMonthYear, mapSchedules, normalizeHolidays, normalizeIssues } from './utils'
import { ConnectedComponentClass } from 'react-redux'
import {Fields} from "../../../shared/modals";
import {
  OVERRIDE_CREATE_SUCCESS,
  OVERRIDE_UPDATE_SUCCESS,
  OVERRIDE_DELETE_SUCCESS,
  OVERRIDE_CREATE_FAILURE,
  OVERRIDE_UPDATE_FAILURE,
  OVERRIDE_DELETE_FAILURE,
  OVERRIDE_SEND_AT_PAST_ERROR,
} from '../constants'

export const COUNTRY_CONFIGURATION_KEY = 'show_holidays_in_calendar'

type OverrideModalMode = 'create' | 'convert' | 'edit' | null

interface OverrideFormData {
  send_date: Date | null,
  send_time: Date | null,
  cutoff_offset: number,
  preprocess_offset: number,
  description: string,
}

const MAX_OFFSET_MINUTES = 10080

interface RCProps {
  hasScheduledContentAccess: boolean,
  issues: UpcomingIssue[],
  countryHolidays: HolidaySchedule[],
  update: (issue) => void,
  onViewDateChange?: (viewDate: Date) => void,
  onCustomize?: (issue: UpcomingIssue) => void,
  onEditOverride?: (issue: UpcomingIssue) => void,
  onDeleteOverride?: (issue: UpcomingIssue) => void,
  onAddExtraIssue?: (date: Date) => void,
}
interface RasaCalendarState {
  saveText: string,
  viewDate: Date,
  selectedDate: Date | null,
}

type RasaCalendarProps = GenericRedux.AllComponentPropsWithModal<any> & RCProps

class RasaCalendarComponent extends React.Component<RasaCalendarProps, RasaCalendarState> {
  private calendarRef: React.RefObject<Calendar>
  constructor(props) {
    super(props)
    this.calendarRef = React.createRef()
    this.state = {
      saveText: '',
      viewDate: new Date(),
      selectedDate: null,
    }
  }

  public render() {
    return (
      <div>
        <div className="date-and-buttons-container">
          <div className="date-container">
            {getMonthYear(this.state.viewDate)}
          </div>
          <div className="buttons-container">
            <div className="today" onClick={this.handleClickTodayButton}>
              Today
            </div>
            <div className="prev" onClick={this.handleClickPreviousButton}>
              {'<'}
            </div>
            <div className="next" onClick={this.handleClickNextButton}>
              {'>'}
            </div>
            {this.props.onAddExtraIssue &&
            <Button color="primary" size="sm" className="add-extra-issue"
              disabled={!this.state.selectedDate || this.state.selectedDate < new Date(new Date().toDateString())}
              onClick={this.handleAddExtraIssue}>
              {this.state.selectedDate
                ? `+ Add Issue (${format(this.state.selectedDate, 'MMM d')})`
                : '+ Add Issue'}
            </Button>}
          </div>
        </div>
        <Calendar
          ref={this.calendarRef}
          height={CALENDAR_HEIGHT}
          view={'month'}
          taskView={false}
          scheduleView={true}
          month={{
            isAlways6Week: false,
            startDayOfWeek: 0,
          }}
          isReadOnly={false}
          useCreationPopup={false}
          useDetailPopup={false}
          onBeforeCreateSchedule={this.handleDateSelect}
          onClickSchedule={this.handleClickSchedule}
          schedules={mapSchedules(this.props.issues, this.props.countryHolidays,
            this.props.hasScheduledContentAccess)}
          usageStatistics={false}
          theme={{
            'month.day.fontSize': '16px',
          }}

          template={{
            monthDayname(model) {
              return String(model.label).toLocaleUpperCase()
            },
            milestone(model) {
              return `<span style="color:#fff;background-color: ${model.bgColor};">${model.title}</span>`;
            },
            allday(schedule) {
              return `<span className='calendar-event-holiday'>${schedule.title}</span>`
            },
          }}
        />
        <CalendarModal
          data={this.props.modals}
          closeModal={this.props.closeModal}
          goTo={this.goTo}
          saveModal={this.props.saveModal}
          title="Update Newsletter"
          update={this.props.update}
          onCustomize={this.props.onCustomize}
          onEditOverride={this.props.onEditOverride}
          onDeleteOverride={this.props.onDeleteOverride}
        />
      </div>
    )
  }

  private handleDateSelect = (e: any) => {
    const selectedDate = new Date(e.start)
    this.setState({ selectedDate }, () => {
      // Clear the creation guide overlay, then highlight the selected cell
      const calendarInstance = this.calendarRef.current.getInstance()
      calendarInstance.render()
      requestAnimationFrame(() => this.highlightSelectedDate())
    })
  }

  private clearSelectedDateHighlight = () => {
    if (!this.calendarRef.current) return
    const root = this.calendarRef.current.getRootElement()
    if (!root) return
    root.querySelectorAll('.selected-date').forEach((el) => el.classList.remove('selected-date'))
  }

  private highlightSelectedDate = () => {
    this.clearSelectedDateHighlight()
    if (!this.state.selectedDate) return

    if (!this.calendarRef.current) return
    const root = this.calendarRef.current.getRootElement()
    if (!root) return

    const cells = root.querySelectorAll('.tui-full-calendar-weekday-grid-line')
    if (!cells.length) return

    // Calculate the first visible date in the month grid
    const viewDate = this.state.viewDate
    const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)
    const dayOfWeek = firstOfMonth.getDay() // 0 = Sunday (matches startDayOfWeek: 0)
    const firstVisible = new Date(firstOfMonth)
    firstVisible.setDate(firstVisible.getDate() - dayOfWeek)

    // Calculate cell index for the selected date
    const sel = this.state.selectedDate
    const selMidnight = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate()).getTime()
    const firstMidnight = new Date(firstVisible.getFullYear(), firstVisible.getMonth(), firstVisible.getDate()).getTime()
    const diffDays = Math.round((selMidnight - firstMidnight) / (86400000))

    if (diffDays >= 0 && diffDays < cells.length) {
      cells[diffDays].classList.add('selected-date')
    }
  }

  private handleAddExtraIssue = () => {
    if (this.state.selectedDate && this.props.onAddExtraIssue) {
      this.props.onAddExtraIssue(this.state.selectedDate)
    }
  }

  private handleClickTodayButton = () => {
    const newViewDate = new Date()
    this.setState({ viewDate: newViewDate, selectedDate: null })
    const calendarInstance = this.calendarRef.current.getInstance()
    calendarInstance.today()
    if (this.props.onViewDateChange) {
      this.props.onViewDateChange(newViewDate)
    }
  }

  private handleClickNextButton = () => {
    const newViewDate = addMonths(this.state.viewDate, 1)
    this.setState({ viewDate: newViewDate, selectedDate: null })
    const calendarInstance = this.calendarRef.current.getInstance()
    calendarInstance.next()
    if (this.props.onViewDateChange) {
      this.props.onViewDateChange(newViewDate)
    }
  }

  private handleClickPreviousButton = () => {
    const newViewDate = addMonths(this.state.viewDate, -1)
    this.setState({ viewDate: newViewDate, selectedDate: null })
    const calendarInstance = this.calendarRef.current.getInstance()
    calendarInstance.prev()
    if (this.props.onViewDateChange) {
      this.props.onViewDateChange(newViewDate)
    }
  }

  private handleClickSchedule = (e) => {
    if (e.schedule.body.updatedIssue && e.schedule.body.updatedIssue.schedule.is_active) {
      this.props.openModal(CalendarModal.key, e)
    }
  }

  private goTo = (location) => {
    this.props.closeModal(CalendarModal.key)
    this.props.push(location)
    }

}

export const RasaCalendar: ConnectedComponentClass<ComponentType<RasaCalendarComponent>, Fields> = GenericRedux.registerNewComponentWithModals<RasaCalendarState>(
  RasaCalendarComponent,
  'rasa_calendar',
  [CalendarModal.key],
  {
    saveText: '',
    viewDate: new Date(),
    selectedDate: null,
  },
)

interface CalendarComponentState {
  activeSchedule: boolean
  hasScheduledContentAccess: boolean
  isLoading: boolean
  isManualSchedule: boolean
  issues: UpcomingIssue[]
  communityIdentifier: string
  communityId: string
  isCountryHolidayDisplay: boolean
  countryHolidays: HolidaySchedule[]
  overrideModalMode: OverrideModalMode
  overrideFormData: OverrideFormData
  selectedIssue: UpcomingIssue | null
  showDeleteConfirmationModal: boolean
  issueToDelete: UpcomingIssue | null
  isMutating: boolean
  formErrors: { [key: string]: string }
}
export class CalendarComponent extends Component<any, CalendarComponentState> {
  public static contextType = RasaContext
  private communityId: string = null
  private sharedStore: SharedStore
  private timezone: string = null

  constructor(props) {
    super(props)
    this.state = {
      activeSchedule: true,
      hasScheduledContentAccess: false,
      isLoading: true,
      isManualSchedule: false,
      issues: [],
      communityIdentifier: null,
      communityId: null,
      isCountryHolidayDisplay: false,
      countryHolidays: [],
      overrideModalMode: null,
      overrideFormData: {
        send_date: null,
        send_time: null,
        cutoff_offset: 0,
        preprocess_offset: 0,
        description: '',
      },
      selectedIssue: null,
      showDeleteConfirmationModal: false,
      issueToDelete: null,
      isMutating: false,
      formErrors: {},
    }
  }

  public componentDidMount = () => {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.person),
    ]).then(([activeCommunity, person]) => {
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
      this.communityId = activeCommunity.communityId
      this.timezone = activeCommunity.data && activeCommunity.data.company_time_zone
          ? activeCommunity.data.company_time_zone
          : DEFAULT_TIMEZONE
      return  Promise.all([
        this.fetchAllIssuesForView(new Date()),
        new Dataset().loadCommunityDataset('countryHolidays', activeCommunity.communityId),
        this.loadData(false)])
      .then(([allIssues, holidays]: [any, any, any]) => {
        this.setState({
          hasScheduledContentAccess: avlFeatures.indexOf(BillingPlanDetailCode.SCHEDULED_CONTENT) > -1,
          issues: normalizeIssues(allIssues, this.timezone),
          isLoading: false,
          activeSchedule: activeCommunity.data.schedule_is_active,
          isManualSchedule: activeCommunity.data.schedule_frequency === ScheduleFrequency.manually,
          communityIdentifier: activeCommunity.communityId,
          communityId: activeCommunity.data.community_id,
          countryHolidays: holidays[0] ? normalizeHolidays(holidays[0], this.timezone) : [],
        })
      })
    })
  }

  public render = () => {
    let description = []
    if (this.state.isManualSchedule) {
      description = [
        'There is no upcoming newsletters',
    ]
    } else {
      description = [
        'A calendar view of when your newsletter is scheduled to send.',
        'Turn on and off specific dates by clicking on them.',
      ]
    }
    return (
      this.state.isLoading ? <Loading size="64"/> :
      <div className="content-calendar">
        <HeaderComponent
          description={description}
          title={SCHEDULE}
          subTitle={UPCOMING_NEWSLETTERS}
        />
        <div>
          {!this.state.isManualSchedule &&
          <div className="holiday-toggle">
            <div className="clickable-item" onClick={() => this.onToggleChange()}>
              <label>US Holidays</label>
              {this.state.isCountryHolidayDisplay
                ? <ToggleOn/>
                : <ToggleOff/>}
            </div>
          </div>}
          {this.state.activeSchedule ?
            <RasaCalendar hasScheduledContentAccess={this.state.hasScheduledContentAccess}
              issues={this.state.issues} update={this.updateIssues}
              countryHolidays={this.state.isCountryHolidayDisplay ? this.state.countryHolidays : []}
              onViewDateChange={this.handleViewDateChange}
              onCustomize={this.handleCustomize}
              onEditOverride={this.handleEditOverride}
              onDeleteOverride={this.handleDeleteOverride}
              onAddExtraIssue={!this.state.isManualSchedule ? this.openCreateModal : undefined}/>
            : <span>Your schedule is currently paused. <a href="/schedule">Click here</a> to turn it back on</span>
          }
        </div>
        {this.renderOverrideModal()}
        {this.renderDeleteConfirmationModal()}
      </div>
    )
  }

  // --- Override modal handlers ---

  private getValidatedTimezone = (): string => {
    return validateTz(this.timezone || DEFAULT_TIMEZONE)
  }

  private getScheduleFromIssues = () => {
    const issue = this.state.issues.find(i => i.schedule && i.schedule.id)
    return issue ? issue.schedule : null
  }

  private openCreateModal = (date: Date) => {
    const schedule = this.getScheduleFromIssues()
    const cutoffOffset = schedule ? parseInt(schedule.cutoff_offset, 10) || 0 : 0
    const preprocessOffset = schedule ? schedule.preprocess_offset || 0 : 0

    this.setState({
      overrideModalMode: 'create',
      overrideFormData: {
        send_date: date,
        send_time: null,
        cutoff_offset: cutoffOffset,
        preprocess_offset: preprocessOffset,
        description: '',
      },
      selectedIssue: null,
      formErrors: {},
    })
  }

  private handleCustomize = (issue: UpcomingIssue) => {
    const schedule = this.getScheduleFromIssues()
    const cutoffOffset = schedule ? parseInt(schedule.cutoff_offset, 10) || 0 : 0
    const preprocessOffset = schedule ? schedule.preprocess_offset || 0 : 0
    const tz = this.getValidatedTimezone()
    const localDate = utcToZonedTime(new Date(issue.date), tz)

    this.setState({
      overrideModalMode: 'convert',
      overrideFormData: {
        send_date: localDate,
        send_time: localDate,
        cutoff_offset: cutoffOffset,
        preprocess_offset: preprocessOffset,
        description: '',
      },
      selectedIssue: issue,
      formErrors: {},
    })
  }

  private handleEditOverride = (issue: UpcomingIssue) => {
    const schedule = this.getScheduleFromIssues()
    const tz = this.getValidatedTimezone()
    const localDate = utcToZonedTime(new Date(issue.date), tz)
    const cutoffOffset = schedule ? parseInt(schedule.cutoff_offset, 10) || 0 : 0

    this.setState({
      overrideModalMode: 'edit',
      overrideFormData: {
        send_date: localDate,
        send_time: localDate,
        cutoff_offset: cutoffOffset,
        preprocess_offset: schedule ? schedule.preprocess_offset || 0 : 0,
        description: issue.schedule_override_description || '',
      },
      selectedIssue: issue,
      formErrors: {},
    })
  }

  private handleDeleteOverride = (issue: UpcomingIssue) => {
    this.setState({
      showDeleteConfirmationModal: true,
      issueToDelete: issue,
    })
  }

  private closeOverrideModal = () => {
    this.setState({
      overrideModalMode: null,
      overrideFormData: {
        send_date: null,
        send_time: null,
        cutoff_offset: 0,
        preprocess_offset: 0,
        description: '',
      },
      selectedIssue: null,
      formErrors: {},
      isMutating: false,
    })
  }

  private closeDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmationModal: false,
      issueToDelete: null,
    })
  }

  private combineDateAndTime = (date: Date, time: Date): Date => {
    const combined = new Date(date)
    combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0)
    return combined
  }

  private validateOverrideForm = (): boolean => {
    const errors: { [key: string]: string } = {}
    const { send_date, send_time, cutoff_offset, preprocess_offset } = this.state.overrideFormData

    if (!send_date) {
      errors.send_time = 'Please select a date on the calendar first'
    } else if (!send_time) {
      errors.send_time = 'Send time is required'
    } else {
      const combined = this.combineDateAndTime(send_date, send_time)
      const tz = this.getValidatedTimezone()
      const utcDate = zonedTimeToUtc(combined, tz)
      if (utcDate <= new Date()) {
        errors.send_time = OVERRIDE_SEND_AT_PAST_ERROR
      }
    }

    if (cutoff_offset < 0 || cutoff_offset > MAX_OFFSET_MINUTES) {
      errors.cutoff_offset = `Must be between 0 and ${MAX_OFFSET_MINUTES}`
    }

    if (preprocess_offset < 0 || preprocess_offset > MAX_OFFSET_MINUTES) {
      errors.preprocess_offset = `Must be between 0 and ${MAX_OFFSET_MINUTES}`
    }

    this.setState({ formErrors: errors })
    return Object.keys(errors).length === 0
  }

  private saveOverride = () => {
    if (!this.validateOverrideForm()) return

    const schedule = this.getScheduleFromIssues()
    if (!schedule) return

    const tz = this.getValidatedTimezone()
    const combined = this.combineDateAndTime(this.state.overrideFormData.send_date, this.state.overrideFormData.send_time)
    const utcDate = zonedTimeToUtc(combined, tz)

    const payload: any = {
      schedule_id: schedule.id,
      send_at: utcDate.toISOString(),
      cutoff_offset: this.state.overrideFormData.cutoff_offset,
      preprocess_offset: this.state.overrideFormData.preprocess_offset,
      is_active: 1,
    }

    if (this.state.overrideFormData.description) {
      payload.description = this.state.overrideFormData.description
    }

    this.setState({ isMutating: true })

    const url = AjaxWrapper.getServerUrl() + `/entity/${this.communityId}/scheduleOverride`
    AjaxWrapper.ajax(url, HttpMethod.POST, payload)
      .then(() => {
        this.context.store.dispatch(Flash.showFlashMessage(OVERRIDE_CREATE_SUCCESS))
        this.closeOverrideModal()
        this.refetchIssues()
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError(OVERRIDE_CREATE_FAILURE))
        this.setState({ isMutating: false })
      })
  }

  private updateOverride = () => {
    if (!this.validateOverrideForm()) return

    const { selectedIssue, overrideFormData } = this.state
    if (!selectedIssue || !selectedIssue.schedule_override_id) return

    const tz = this.getValidatedTimezone()
    const combined = this.combineDateAndTime(overrideFormData.send_date, overrideFormData.send_time)
    const utcDate = zonedTimeToUtc(combined, tz)

    const payload: any = {
      send_at: utcDate.toISOString(),
      cutoff_offset: overrideFormData.cutoff_offset,
    }

    if (overrideFormData.description !== undefined) {
      payload.description = overrideFormData.description
    }

    this.setState({ isMutating: true })

    const url = AjaxWrapper.getServerUrl() +
      `/entity/${this.communityId}/scheduleOverride/${selectedIssue.schedule_override_id}`
    AjaxWrapper.ajax(url, HttpMethod.PUT, payload)
      .then(() => {
        this.context.store.dispatch(Flash.showFlashMessage(OVERRIDE_UPDATE_SUCCESS))
        this.closeOverrideModal()
        this.refetchIssues()
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError(OVERRIDE_UPDATE_FAILURE))
        this.setState({ isMutating: false })
      })
  }

  private deleteOverride = () => {
    const { issueToDelete } = this.state
    if (!issueToDelete || !issueToDelete.schedule_override_id) return

    this.setState({ isMutating: true })

    const url = AjaxWrapper.getServerUrl() +
      `/entity/${this.communityId}/scheduleOverride/${issueToDelete.schedule_override_id}`
    AjaxWrapper.ajax(url, HttpMethod.DELETE, {})
      .then(() => {
        this.context.store.dispatch(Flash.showFlashMessage(OVERRIDE_DELETE_SUCCESS))
        this.closeDeleteConfirmation()
        this.setState({ isMutating: false })
        this.refetchIssues()
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError(OVERRIDE_DELETE_FAILURE))
        this.setState({ isMutating: false })
      })
  }

  private getModalTitle = (): string => {
    switch (this.state.overrideModalMode) {
      case 'create': return 'Add Issue'
      case 'convert': return 'Customize Issue'
      case 'edit': return 'Edit Override'
      default: return ''
    }
  }

  private renderOverrideModal = (): JSX.Element => {
    const { overrideModalMode, overrideFormData, formErrors, isMutating } = this.state
    const tz = this.getValidatedTimezone()
    const dateLabel = overrideFormData.send_date
      ? format(overrideFormData.send_date, 'EEEE, MMMM d, yyyy')
      : 'No date selected'

    return (
      <Modal isOpen={overrideModalMode !== null} toggle={this.closeOverrideModal}>
        <ModalHeader toggle={this.closeOverrideModal}>
          {this.getModalTitle()}
        </ModalHeader>
        <ModalBody>
          {isMutating ? <Loading size="64" /> :
            <div className="override-form">
              <div className="override-form-row">
                <label>Date</label>
                <div className="override-date-display">{dateLabel}</div>
              </div>
              <div className="override-form-row">
                <label>Send Time ({tz})</label>
                <TimePicker
                  value={overrideFormData.send_time}
                  onChange={(e) => this.setState({
                    overrideFormData: { ...overrideFormData, send_time: e.value },
                    formErrors: { ...formErrors, send_time: undefined },
                  })}
                />
                {formErrors.send_time && <div className="form-error">{formErrors.send_time}</div>}
              </div>
              <div className="override-form-row">
                <label>Cutoff Offset (minutes before send)</label>
                <Input
                  type="number"
                  min={0}
                  max={MAX_OFFSET_MINUTES}
                  value={overrideFormData.cutoff_offset}
                  onChange={(e) => this.setState({
                    overrideFormData: { ...overrideFormData, cutoff_offset: parseInt(e.target.value, 10) || 0 },
                    formErrors: { ...formErrors, cutoff_offset: undefined },
                  })}
                />
                {formErrors.cutoff_offset && <div className="form-error">{formErrors.cutoff_offset}</div>}
              </div>
              <div className="override-form-row">
                <label>Preprocess Offset (minutes before send)</label>
                <Input
                  type="number"
                  min={0}
                  max={MAX_OFFSET_MINUTES}
                  value={overrideFormData.preprocess_offset}
                  onChange={(e) => this.setState({
                    overrideFormData: { ...overrideFormData, preprocess_offset: parseInt(e.target.value, 10) || 0 },
                    formErrors: { ...formErrors, preprocess_offset: undefined },
                  })}
                />
                {formErrors.preprocess_offset && <div className="form-error">{formErrors.preprocess_offset}</div>}
              </div>
              <div className="override-form-row">
                <label>Description (optional)</label>
                <Input
                  type="text"
                  value={overrideFormData.description}
                  onChange={(e) => this.setState({
                    overrideFormData: { ...overrideFormData, description: e.target.value },
                  })}
                />
              </div>
            </div>
          }
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={isMutating}
            onClick={overrideModalMode === 'edit' ? this.updateOverride : this.saveOverride}
          >
            Save
          </Button>
          <Button onClick={this.closeOverrideModal} disabled={isMutating}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private renderDeleteConfirmationModal = (): JSX.Element => {
    return (
      <Modal isOpen={this.state.showDeleteConfirmationModal}>
        <ModalBody className="delete-modal">
          <div>
            <h4>Are You Sure?</h4>
            {this.state.isMutating ? <Loading size="64" /> :
              <p>Are you sure you want to delete this override?<br/>
                The computed issue will be restored if one exists for this date.</p>
            }
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={this.deleteOverride} disabled={this.state.isMutating}>
            Yes
          </Button>
          <Button onClick={this.closeDeleteConfirmation} disabled={this.state.isMutating}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  // --- Existing methods ---

  private refetchIssues = () => {
    this.fetchAllIssuesForView(new Date()).then((allIssues) => {
      this.setState({
        issues: normalizeIssues(allIssues, this.timezone),
      })
    })
  }

  private handleViewDateChange = (viewDate: Date) => {
    this.fetchAllIssuesForView(viewDate).then((allIssues) => {
      this.setState({
        issues: normalizeIssues(allIssues, this.timezone),
      })
    })
  }

  private fetchAllIssuesForView = (viewDate: Date): Promise<UpcomingIssue[]> => {
    const now = new Date()
    const currentMonthStart = startOfMonth(now)
    const nextMonthStart = startOfMonth(addMonths(now, 1))
    const rangeStart = formatDateForES(startOfMonth(addMonths(viewDate, -6)))
    const rangeEnd = formatDateForES(endOfMonth(addMonths(viewDate, 6)))

    if (viewDate >= nextMonthStart) {
      // Future month: only upcoming issues
      return this.fetchUpcomingIssuesForRange(rangeStart, rangeEnd)
    } else if (isBefore(viewDate, currentMonthStart)) {
      // Past month: only past issues
      return this.fetchPastIssuesForRange(rangeStart, rangeEnd)
    } else {
      // Current month: both, then deduplicate
      return Promise.all([
        this.fetchUpcomingIssuesForRange(rangeStart, rangeEnd),
        this.fetchPastIssuesForRange(rangeStart, rangeEnd),
      ]).then(([upcomingIssues, pastIssues]) => {
        const upcomingIds = new Set(upcomingIssues.map(issue => issue.id || issue.holiday_id))
        const filteredPastIssues = pastIssues.filter(issue =>
          !upcomingIds.has(issue.id || issue.holiday_id)
        )
        return [...filteredPastIssues, ...upcomingIssues]
      })
    }
  }

  private fetchUpcomingIssuesForRange = (startDate: string, endDate: string): Promise<UpcomingIssue[]> => {
    const params = [
      { param: 'startDate', value: startDate },
      { param: 'endDate', value: endDate },
    ]
    return new Dataset().loadCommunityDataset('communityUpcomingIssues', this.communityId, params)
      .then((response: any) => {
        if (response && response.length && response[0]) {
          return response[0]
        }
        return []
      })
  }

  private fetchPastIssuesForRange = (startDate: string, endDate: string): Promise<any[]> => {
    const params = [
      { param: 'startDate', value: startDate },
      { param: 'endDate', value: endDate },
      { param: 'startPage', value: 0 },
      { param: 'pageSize', value: 500 },
    ]
    return new Dataset().loadCommunityDataset('communityIssues', this.communityId, params)
      .then((issuesResponse: any) => {
        if (issuesResponse && issuesResponse.length) {
          return issuesResponse[0].map(issue => {
            const issueDate = issue.send_at || issue.date
            return {
              ...issue,
              date: issueDate,
              is_active: issue.status !== 'Canceled',
              holiday_id: issue.id,
              schedule: {
                is_active: true,
                frequency: issue.frequency || 'weekly'
              }
            }
          })
        }
        return []
      })
  }

  private onToggleChange = () => {
    return this.context.entityMetadata.getEntityObject('community_configuration')
    .then((entity) => {
      entity.setRecordId(this.state.communityIdentifier, null)
      entity.data.community_id = this.state.communityId
      entity.data.key = COUNTRY_CONFIGURATION_KEY
      entity.data.value = !this.state.isCountryHolidayDisplay
      entity.data.is_active = true

      return entity.save().then(() => {
        this.setState({isCountryHolidayDisplay: !this.state.isCountryHolidayDisplay})
        this.loadData(true)
      })
    })
  }

  private loadData = (forceRefresh: boolean) => {
    const params = [
      {param: 'key', value: COUNTRY_CONFIGURATION_KEY},
      {param: 'communityId', value: this.communityId},
      {param: 'forceRefresh', value: forceRefresh},
    ]
    return new Dataset().loadCommunityDataset('communityConfiguration', this.communityId, params)
      .then((configuration) => {
        if (configuration.length > 0 && configuration[0].length > 0) {
          this.setState({
            isCountryHolidayDisplay: isTruthy(configuration[0][0].value),
          })
        }
      })
  }

  private updateIssues = (issue: UpcomingIssue) => {
    const updatedIssues = this.state.issues.map((i: UpcomingIssue) => {
      return {
        ...i,
        is_active: issue.date === i.date ? !i.is_active : i.is_active,
      }
    })

    return this.context.entityMetadata.getEntityObject('community_holiday')
      .then((entity) => {
        entity.setRecordId(this.communityId, issue.holiday_id)
        entity.data.is_active = issue.is_active
        if (isNil(issue.holiday_id)) {
          // note that issue.issue_date is a Date object which value is in the newsletter timezone
          entity.data.holiday = format(issue.issue_date, 'yyyy-MM-dd')
          entity.data.name = issue.date
          entity.data.community_identifier = this.communityId
        }
        return entity.save()
      })
      .then(() => this.setState({ issues: updatedIssues }))
      .catch((err) =>  Sentry.captureException(err))
  }
}
